import { useState, useEffect, useCallback, useDeferredValue, useRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@reflex/ui/components/ui/command';
import { GitBranch, Lock, ChevronsUpDown, X, Loader2, Star, Github, Globe } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { useGitHubRepos, useGitHubBranches, deriveGitHubConnectionState } from './useGitHubRepos';
import { useFavoriteRepos } from './useFavoriteRepos';
import { useRepoSelectionHistory } from './useRepoSelectionHistory';
import { GitHubAppRequiredDialog } from './GitHubAppRequiredDialog';
import { timeAgo } from '@reflex/ui/lib/format';
import { getApiUrl } from '@reflex/ui/client/http';
import { toRepoSlug } from '@reflex/plugin-utils/github-utils';
import type { RepoAccess } from '@runloop/reflex-contract';
import type { GitHubRepo } from '@reflex/shared';

interface RepoSelectorProps {
  repoSlug: string;
  branch: string;
  /**
   * Fired when the repo changes. `options.access` reports how the repo was
   * picked: `connected` for a repo chosen from the connected GitHub list, or
   * `public` when the user opts into the inline public-repo entry (only
   * possible when `allowPublicRepos` is set). Callers that don't support
   * public mounts can ignore the second argument.
   */
  onRepoChange: (repoSlug: string, options?: { access?: RepoAccess }) => void;
  onBranchChange: (branch: string) => void;
  /**
   * Controls how the repo and branch controls are arranged.
   *
   *   - `inline` (default): side by side — repo flexes, branch is a fixed
   *     width. Suited to wide form rows.
   *   - `stacked`: repo above branch, each full width. Suited to narrow
   *     containers like the attachment popover, where an inline branch would
   *     squeeze the repo slug into an ellipsis.
   */
  layout?: 'inline' | 'stacked';
  /**
   * When set, typing an `owner/repo` (or a GitHub URL) that has no connected
   * match surfaces a "use public repo" option in the dropdown. Selecting it
   * reports `access: 'public'`, mounting the repo with an anonymous clone
   * instead of a GitHub App installation token.
   */
  allowPublicRepos?: boolean;
  /**
   * Access mode of the currently selected repo. Defaults to `connected`. When
   * `public`, branch listing is suppressed (the connected branch API requires
   * an installation token the public repo doesn't have).
   */
  repoAccess?: RepoAccess;
  /**
   * Which repo list to browse. `user` (default) lists repos the caller's
   * personal GitHub OAuth token can see. `installation` lists repos the
   * org's GitHub App installation(s) can see — use this when the picked
   * repo will later be cloned with an installation token rather than the
   * picking user's own token (e.g. Blueprint builds), so what's shown here
   * matches what will actually be clonable.
   */
  scope?: 'user' | 'installation';
}

export function RepoSelector({
  repoSlug,
  branch,
  onRepoChange,
  onBranchChange,
  layout = 'inline',
  allowPublicRepos = false,
  repoAccess = 'connected',
  scope = 'user',
}: RepoSelectorProps) {
  const stacked = layout === 'stacked';
  const isPublic = repoAccess === 'public';
  const [repoOpen, setRepoOpen] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { favorites, isFavorite, toggleFavorite } = useFavoriteRepos();
  // Every pick feeds the ranking the next open is ordered by (see
  // `shared/repo-ranking.ts`).
  const { recordSelection } = useRepoSelectionHistory();

  // When rendered inside a Radix Dialog, `react-remove-scroll` locks wheel
  // events outside the dialog's shard. Popovers default to portaling into
  // `document.body`, which falls outside that shard and freezes the cmdk
  // list scroll. Portal into the dialog content instead so wheel events
  // reach the list. Falls back to body portal when no dialog ancestor.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const host = wrapperRef.current?.closest('[data-slot="dialog-content"]');
    setPortalContainer((host as HTMLElement | null) ?? null);
  }, []);

  // One query per (org, scope) backs the whole picker: it is fetched on mount
  // (so the list is already warm when the popover opens) and every keystroke
  // filters it in the browser rather than issuing a request. `useDeferredValue`
  // keeps a long list's re-render off the input's critical path — that is a
  // render-scheduling hint, not a debounce; there is nothing left to debounce.
  const deferredSearch = useDeferredValue(search);
  const reposResult = useGitHubRepos(deferredSearch, true, scope);
  const { data: repos, isFetching: reposFetching } = reposResult;

  const {
    tokenMissing: githubTokenMissing,
    needsReconnect: githubNeedsReconnect,
    needsApp: needsGitHubApp,
  } = deriveGitHubConnectionState(reposResult, scope);
  const isSearching = search !== deferredSearch || reposFetching;

  // Installation-scoped pickers list what the org's GitHub App can clone. With
  // no installation the list is necessarily empty, so the empty state is the
  // only place the user can act — make it open the shared install prompt rather
  // than telling them to go find an admin.
  //
  // Connecting your own GitHub account outranks this: it is self-serve, while
  // installing the org App is an admin action. Prompting an end user to do an
  // admin's job before they have even linked their own account is the wrong
  // ask, so `githubNeedsReconnect` wins (see the render below).
  const [installDialogOpen, setInstallDialogOpen] = useState(false);

  const displayRepos = repos;

  const showFavorites = favorites.length > 0 && !search;

  // The inline public-repo affordance: when the typed query parses to a valid
  // `owner/repo` (shorthand or GitHub URL) and no connected repo matches it,
  // offer mounting it as a public repo. This works even when GitHub isn't
  // connected at all, which is the whole point — you can reach a public repo
  // without a GitHub App installation.
  const publicCandidate = allowPublicRepos ? toRepoSlug(search) : null;
  const showPublicOption =
    !!publicCandidate &&
    publicCandidate !== repoSlug &&
    !displayRepos?.some((r) => r.fullName === publicCandidate);

  const handleRepoOpenChange = useCallback((open: boolean) => {
    setRepoOpen(open);
    // Only (re)set the filter when opening. Resetting on close would mutate
    // the still-mounted list during Radix's close animation, briefly flashing
    // the unfiltered repos before the popover unmounts.
    if (open) {
      setSearch('');
    }
  }, []);

  const repoParts = repoSlug ? repoSlug.split('/') : [];
  const repoOwner = repoParts.length >= 2 ? repoParts[repoParts.length - 2] : null;
  const repoName = repoParts.length >= 1 ? repoParts[repoParts.length - 1] : null;

  // A public repo has no installation token, so the connected branch API would
  // 401/404. Skip the fetch and let the clone fall back to the default branch.
  const { data: branches, isLoading: branchesLoading } = useGitHubBranches(
    repoSlug && !isPublic ? repoOwner : null,
    repoSlug && !isPublic ? repoName : null,
    scope,
  );

  const selectedRepo = displayRepos?.find((r) => r.fullName === repoSlug);

  const handleSelectRepo = useCallback(
    (repo: GitHubRepo) => {
      recordSelection(repo.fullName);
      onRepoChange(repo.fullName, { access: 'connected' });
      onBranchChange(repo.defaultBranch);
      // Leave the search untouched on close; it is reset on the next open. See
      // handleRepoOpenChange for why we avoid mutating during the close anim.
      setRepoOpen(false);
    },
    [onRepoChange, onBranchChange, recordSelection],
  );

  const handleSelectPublic = useCallback(() => {
    if (!publicCandidate) return;
    // Deliberately NOT recorded in the pick history: a public repo is typed,
    // not chosen from the list, so its key can never match a row — all it
    // would do is inflate the `maxCount` the popularity term is measured
    // against, flattening the ranking of every repo that IS in the list.
    onRepoChange(publicCandidate, { access: 'public' });
    // No connected branch list for public repos — clear the branch so the
    // clone uses the repo's default.
    onBranchChange('');
    setRepoOpen(false);
  }, [publicCandidate, onRepoChange, onBranchChange]);

  const handleClearRepo = useCallback(() => {
    onRepoChange('', { access: 'connected' });
    onBranchChange('');
  }, [onRepoChange, onBranchChange]);

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'flex gap-1.5',
        // Inline: size to content (capped) so a short slug doesn't stretch the
        // control across the whole row. Stacked: each control spans its column.
        stacked ? 'flex-col items-stretch' : 'w-fit max-w-full items-center',
      )}
    >
      <Popover open={repoOpen} onOpenChange={handleRepoOpenChange}>
        {/* The bordered box owns the focus ring (via focus-within) so the
            trigger and the clear button can sit side by side as a flush group
            at the end, instead of overlapping an absolutely-positioned X. */}
        <div
          className={cn(
            'relative flex h-8 min-w-0 items-center rounded-md border border-input bg-transparent text-xs shadow-xs',
            'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
            'dark:bg-input/30 dark:hover:bg-input/50 transition-[color,box-shadow]',
            stacked ? 'w-full' : 'min-w-0 flex-1',
          )}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Select repository"
              className={cn(
                'flex h-full min-w-0 flex-1 items-center gap-1.5 rounded-md pl-2.5 outline-none',
                repoSlug ? 'pr-1' : 'pr-2.5',
                !repoSlug && 'text-muted-foreground',
              )}
            >
              {isPublic && repoSlug ? (
                <Globe className="h-3 w-3 shrink-0" />
              ) : (
                <GitBranch className="h-3 w-3 shrink-0" />
              )}
              <span className="min-w-0 truncate">
                {repoSlug ? repoSlug : 'Select repository...'}
              </span>
              {isPublic && repoSlug && (
                <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Public
                </span>
              )}
              <ChevronsUpDown className="ml-auto h-3 w-3 shrink-0 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          {repoSlug && (
            <button
              type="button"
              aria-label="Clear repository"
              className="mr-1.5 shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                handleClearRepo();
              }}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <PopoverContent
          className="w-[--radix-popover-trigger-width]! min-w-72 p-0"
          align="start"
          container={portalContainer ?? undefined}
        >
          <Command shouldFilter={false}>
            <div className="relative">
              <CommandInput
                value={search}
                onValueChange={setSearch}
                placeholder="Search repos..."
                className="text-xs"
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 h-3 w-3 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
            </div>
            <CommandList className="max-h-64">
              {githubNeedsReconnect ? (
                <div
                  className="flex flex-col items-center gap-2 px-4 py-6 text-center"
                  data-testid={
                    githubTokenMissing ? 'github-reconnect-required' : 'github-not-connected'
                  }
                >
                  <Github className="h-6 w-6 text-muted-foreground" />
                  <p className="text-xs font-medium">
                    {githubTokenMissing
                      ? 'GitHub access token expired'
                      : 'GitHub account not connected'}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {githubTokenMissing
                      ? 'Reconnect to refresh your access token and browse repositories.'
                      : 'Connect your GitHub account to browse and select repositories.'}
                  </p>
                  <a
                    href={getApiUrl('/auth/github')}
                    className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    <Github className="h-3.5 w-3.5" />
                    {githubTokenMissing ? 'Reconnect GitHub' : 'Connect GitHub'}
                  </a>
                </div>
              ) : null}
              {!githubNeedsReconnect && needsGitHubApp ? (
                <div
                  className="flex flex-col items-center gap-2 px-4 py-6 text-center"
                  data-testid="github-app-required"
                >
                  <Github className="h-6 w-6 text-muted-foreground" />
                  <p className="text-xs font-medium">No GitHub App installation</p>
                  <p className="text-[11px] text-muted-foreground">
                    This organization has no GitHub App installed, so there are no repositories to
                    build from.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setRepoOpen(false);
                      setInstallDialogOpen(true);
                    }}
                    className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    <Github className="h-3.5 w-3.5" />
                    Set up the GitHub App
                  </button>
                </div>
              ) : null}
              <CommandEmpty className="py-6 text-center text-xs text-muted-foreground">
                {githubNeedsReconnect || needsGitHubApp
                  ? ''
                  : isSearching
                    ? 'Searching...'
                    : scope === 'installation'
                      ? "No repos found. Ask an org admin to check the GitHub App installation's repository access if you don't see the expected repo."
                      : 'No repos found'}
              </CommandEmpty>
              {selectedRepo && (
                <CommandGroup heading="Selected">
                  <RepoItem
                    repo={selectedRepo}
                    selected
                    favorited={isFavorite(selectedRepo.fullName)}
                    onSelect={() => handleSelectRepo(selectedRepo)}
                    onToggleFavorite={() => toggleFavorite(selectedRepo)}
                  />
                </CommandGroup>
              )}
              {isPublic && repoSlug && (
                <CommandGroup heading="Selected">
                  <CommandItem
                    value={`__public_selected__${repoSlug}`}
                    onSelect={() => setRepoOpen(false)}
                    className="gap-2 px-3 py-2 text-xs bg-primary/10 shadow-[inset_2px_0_0_0_var(--color-primary)]"
                    data-testid="repo-public-selected"
                  >
                    <Globe className="h-3 w-3 shrink-0 text-primary" />
                    <span className="min-w-0 truncate font-medium text-primary">{repoSlug}</span>
                    <span className="ml-auto shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Public
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
              {showFavorites &&
                favorites.filter((repo) => repo.fullName !== repoSlug).length > 0 && (
                  <CommandGroup heading="Favorites">
                    {favorites
                      .filter((repo) => repo.fullName !== repoSlug)
                      .map((repo) => (
                        <RepoItem
                          key={`fav-${repo.fullName}`}
                          repo={repo}
                          selected={false}
                          favorited
                          onSelect={() => handleSelectRepo(repo)}
                          onToggleFavorite={() => toggleFavorite(repo)}
                        />
                      ))}
                  </CommandGroup>
                )}
              {displayRepos &&
                displayRepos.filter(
                  (repo) =>
                    repo.fullName !== repoSlug && (!showFavorites || !isFavorite(repo.fullName)),
                ).length > 0 && (
                  <CommandGroup heading={showFavorites ? 'Results' : undefined}>
                    {displayRepos
                      .filter(
                        (repo) =>
                          repo.fullName !== repoSlug &&
                          (!showFavorites || !isFavorite(repo.fullName)),
                      )
                      .map((repo) => (
                        <RepoItem
                          key={repo.fullName}
                          repo={repo}
                          selected={false}
                          favorited={isFavorite(repo.fullName)}
                          onSelect={() => handleSelectRepo(repo)}
                          onToggleFavorite={() => toggleFavorite(repo)}
                        />
                      ))}
                  </CommandGroup>
                )}
              {showPublicOption && (
                <CommandGroup heading="Public repository">
                  <CommandItem
                    value={`__public__${publicCandidate}`}
                    onSelect={handleSelectPublic}
                    className="gap-2 px-3 py-2 text-xs"
                    data-testid="repo-public-option"
                  >
                    <Globe className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 truncate font-medium">
                      Use public repo{' '}
                      <span className="font-mono text-foreground">{publicCandidate}</span>
                    </span>
                    <span className="ml-auto shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Public
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {repoSlug && !isPublic && (
        <Popover open={branchOpen} onOpenChange={setBranchOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Select branch"
              className={cn(
                'flex h-8 items-center justify-between gap-1.5 rounded-md border border-input bg-transparent px-2.5 text-xs shadow-xs',
                // Hug the branch name (capped + truncating) rather than reserving
                // a fixed 160px that dwarfs short names like "main".
                stacked ? 'w-full' : 'w-fit min-w-0 max-w-[14rem]',
                'outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
                'dark:bg-input/30 dark:hover:bg-input/50 transition-[color,box-shadow]',
                !branch && 'text-muted-foreground',
              )}
            >
              <span className="truncate">
                {branch || (selectedRepo?.defaultBranch ?? 'Select branch...')}
              </span>
              <ChevronsUpDown className="h-3 w-3 shrink-0 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width]! min-w-52 p-0"
            align="start"
            container={portalContainer ?? undefined}
          >
            <Command>
              <CommandInput placeholder="Filter branches..." className="text-xs" />
              <CommandList className="max-h-52">
                {branchesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    <CommandEmpty className="py-6 text-center text-xs text-muted-foreground">
                      No branches found
                    </CommandEmpty>
                    {branches && branches.length > 0 && (
                      <CommandGroup>
                        {branches.map((b) => (
                          <CommandItem
                            key={b.name}
                            value={b.name}
                            onSelect={() => {
                              onBranchChange(b.name);
                              setBranchOpen(false);
                            }}
                            className={cn(
                              'px-2 py-1.5 text-xs',
                              b.name === branch &&
                                'bg-primary/10 font-medium text-primary shadow-[inset_2px_0_0_0_var(--color-primary)]',
                            )}
                          >
                            {b.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      {/* Rendered at the wrapper root, not inside the Popover: opening it closes
          the popover, which would unmount a dialog nested inside it. */}
      <GitHubAppRequiredDialog
        open={installDialogOpen}
        onOpenChange={setInstallDialogOpen}
        reason="build from your organization's repositories"
      />
    </div>
  );
}

function RepoItem({
  repo,
  selected,
  favorited,
  onSelect,
  onToggleFavorite,
}: {
  repo: GitHubRepo;
  selected: boolean;
  favorited: boolean;
  onSelect: () => void;
  onToggleFavorite: () => void;
}) {
  return (
    <CommandItem
      value={repo.fullName}
      onSelect={onSelect}
      className={cn(
        'group/row gap-2 px-3 py-2 text-xs',
        selected && 'bg-primary/10 shadow-[inset_2px_0_0_0_var(--color-primary)]',
      )}
    >
      <span className={cn('min-w-0 truncate font-medium', selected && 'text-primary')}>
        {repo.fullName}
      </span>
      {repo.private && <Lock className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />}
      <button
        type="button"
        className={cn(
          'shrink-0 rounded p-0.5 transition-colors',
          favorited
            ? 'text-status-warning/60 hover:text-status-warning'
            : 'text-muted-foreground/30 opacity-0 group-hover/row:opacity-100 hover:text-status-warning',
        )}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.stopPropagation();
        }}
        aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
      >
        <Star className={cn('h-3 w-3', favorited && 'fill-current')} />
      </button>
      <div className="ml-auto flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[10px] text-muted-foreground">
        {repo.language && <span>{repo.language}</span>}
        <span>{timeAgo(new Date(repo.updatedAt).getTime(), { short: true })}</span>
      </div>
    </CommandItem>
  );
}
